import { useEffect, useRef, useState } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@/components/UserProvider";
import { mailIntakeQueryOptions } from "@/lib/data/react-query/mail-intake";
import {
  uploadFile,
  fileOperationMessage,
} from "@/lib/data/requests/files/canonical-files";
import { invalidateFileQueries } from "@/lib/data/react-query/files/canonical-files";
import { libraryQueryKey } from "@/lib/data/react-query/file-library";
import { queryKeys } from "@/lib/data/static/query-keys";

export type LocalMailUpload = {
  key: string;
  name: string;
  reservedId?: string;
  error?: string;
};

export function useMailIntake() {
  const { currentUser } = useUser();
  const storageKey = `mail-intake:${currentUser.firmId}`;
  const [batchId, setBatch] = useState<string | undefined>(
    () => sessionStorage.getItem(storageKey) ?? undefined,
  );
  const [uploads, setUploads] = useState<LocalMailUpload[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string>();
  const client = useQueryClient();
  const query = useInfiniteQuery(
    mailIntakeQueryOptions(currentUser.firmId, batchId),
  );
  const items = query.data?.pages.flatMap((page) => page.data) ?? [];
  const observed = useRef(new Map<string, string>());
  useEffect(() => {
    const rows = query.data?.pages.flatMap((page) => page.data) ?? [];
    let changed = false;
    for (const row of rows) {
      const state = `${row.status}:${row.revision}`;
      if (row.status !== "uploading" && observed.current.get(row.id) !== state)
        changed = true;
      observed.current.set(row.id, state);
    }
    if (changed) {
      void invalidateFileQueries(client);
      void client.invalidateQueries({ queryKey: libraryQueryKey });
    }
  }, [query.data, client]);
  const refresh = () =>
    client.invalidateQueries({
      queryKey: [queryKeys.mailIntake, currentUser.firmId],
    });
  const setBatchId = (id?: string) => {
    setBatch(id);
    if (id) sessionStorage.setItem(storageKey, id);
    else sessionStorage.removeItem(storageKey);
  };
  const upload = async (files: File[]) => {
    if (!files.length || uploading) return;
    if (files.length > 100) {
      setUploadError(
        "Choose up to 100 files at once. No files from this selection were uploaded.",
      );
      return;
    }
    const batch = crypto.randomUUID();
    const pending = files.map((file) => ({
      key: crypto.randomUUID(),
      name: file.name,
    }));
    setUploads(pending);
    setBatchId(batch);
    setUploadError(undefined);
    setUploading(true);
    let next = 0;
    const update = (key: string, values: Partial<LocalMailUpload>) => {
      setUploads((rows) =>
        rows.map((row) => (row.key === key ? { ...row, ...values } : row)),
      );
    };
    try {
      await Promise.all(
        Array.from({ length: Math.min(3, files.length) }, async () => {
          while (next < files.length) {
            const index = next++;
            const row = pending[index];
            try {
              await uploadFile({
                file: files[index],
                name: row.name,
                destination: { scope: "firm" },
                expectedMode: "create",
                organizeAfterProcessing: true,
                intakeBatchId: batch,
                onUploadReserved: (id) => {
                  update(row.key, { reservedId: id });
                  void refresh();
                },
              });
            } catch (error) {
              update(row.key, {
                error: fileOperationMessage(
                  error,
                  "This file could not be uploaded. Select it again to retry.",
                ),
              });
            } finally {
              await refresh();
            }
          }
        }),
      );
    } finally {
      setUploading(false);
    }
  };
  const localUploads = uploads.filter((row) => {
    const saved = items.find((item) => item.id === row.reservedId);
    // A committed publication can lose its completion receipt after immediate
    // filing. The durable row is the source of truth, even when the API replay
    // is no longer readable to this uploader at the new matter destination.
    return !saved || (saved.status === "uploading" && !!row.error);
  });
  return {
    query,
    items,
    batchId,
    setBatchId,
    uploading,
    uploadError,
    localUploads,
    upload,
    refresh,
  };
}
